"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import MagneticButton from "./MagneticButton";
import AmbientCanvas from "./AmbientCanvas";

gsap.registerPlugin(ScrollTrigger);

interface RitualStep {
  num: string;
  preheading: string;
  heading: string;
  body: string;
  accent: string;
  stat: string;
  statLabel: string;
}

const steps: RitualStep[] = [
  {
    num: "01",
    preheading: "The Harvest",
    heading: "Born from Darkness",
    body: "Rare botanicals gathered under moonlight from the world's most remote corners — high-altitude oud forests, volcanic amber beds, blooms that open only after dusk.",
    accent: "#a8843a",
    stat: "47",
    statLabel: "Rare Ingredients",
  },
  {
    num: "02",
    preheading: "The Craft",
    heading: "Distilled by Obsession",
    body: "Our master perfumers work in silence. No formulas, no blueprints. Only instinct, memory, and hundreds of iterations until an accord whispers truth.",
    accent: "#c47a8b",
    stat: "6mo",
    statLabel: "Per Blend",
  },
  {
    num: "03",
    preheading: "The Vessel",
    heading: "Architecture of Allure",
    body: "Ribbed glass that catches light, gold labels bearing our spider-gem crest, weighted caps. Every detail designed to be remembered long after you leave.",
    accent: "#5b7d9b",
    stat: "12h+",
    statLabel: "Longevity",
  },
];

export default function Ritual({ onOrderClick }: { onOrderClick?: () => void }) {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (!sectionRef.current) return;

    const ctx = gsap.context(() => {
      // Header reveal
      gsap.from(".ritual-eyebrow", {
        y: 22,
        opacity: 0,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".ritual-eyebrow",
          start: "top 88%",
          toggleActions: "play none none none",
        },
      });

      gsap.from(".ritual-title", {
        y: 28,
        opacity: 0,
        filter: "blur(8px)",
        duration: 1.2,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".ritual-title",
          start: "top 88%",
          toggleActions: "play none none none",
        },
      });

      gsap.from(".ritual-sub", {
        y: 18,
        opacity: 0,
        duration: 1,
        delay: 0.15,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".ritual-sub",
          start: "top 92%",
          toggleActions: "play none none none",
        },
      });

      // Vertical line draws itself on scroll
      gsap.from(".ritual-line", {
        scaleY: 0,
        transformOrigin: "top center",
        ease: "none",
        scrollTrigger: {
          trigger: ".ritual-line",
          start: "top 82%",
          end: "bottom 55%",
          scrub: 1.2,
        },
      });

      // Timeline nodes pop in with a golden glow
      gsap.utils.toArray<HTMLElement>(".ritual-node").forEach((node, i) => {
        gsap.from(node, {
          scale: 0,
          opacity: 0,
          duration: 0.55,
          delay: i * 0.18,
          ease: "back.out(2.2)",
          scrollTrigger: {
            trigger: node,
            start: "top 90%",
            toggleActions: "play none none none",
          },
        });

        gsap.to(node, {
          boxShadow: "0 0 0 7px rgba(201,168,76,0.22)",
          repeat: -1,
          yoyo: true,
          duration: 2.4,
          ease: "sine.inOut",
          delay: i * 0.4,
          scrollTrigger: {
            trigger: node,
            start: "top 90%",
          },
        });
      });

      // Step rows slide in alternately from left / right
      gsap.utils.toArray<HTMLElement>(".ritual-step").forEach((step, i) => {
        const fromLeft = i % 2 === 0;

        gsap.from(step, {
          x: fromLeft ? -90 : 90,
          opacity: 0,
          duration: 1.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: step,
            start: "top 85%",
            toggleActions: "play none none none",
          },
        });
      });

      // Stats drift slowly
      gsap.utils.toArray<HTMLElement>(".ritual-stat").forEach((stat, i) => {
        gsap.to(stat, {
          y: i % 2 === 0 ? 7 : -7,
          duration: 2.8 + i * 0.4,
          ease: "sine.inOut",
          yoyo: true,
          repeat: -1,
          scrollTrigger: {
            trigger: stat,
            start: "top 92%",
          },
        });
      });

      // Closing CTA reveal
      gsap.from(".ritual-cta-eyebrow", {
        y: 16,
        opacity: 0,
        duration: 0.9,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".ritual-cta-eyebrow",
          start: "top 92%",
          toggleActions: "play none none none",
        },
      });

      gsap.from(".ritual-cta-actions", {
        y: 24,
        opacity: 0,
        duration: 1.1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".ritual-cta-actions",
          start: "top 94%",
          toggleActions: "play none none none",
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="ritual"
      aria-label="The Ritual — Our Craft"
      className="relative overflow-hidden"
      style={{ background: "linear-gradient(180deg, #f9f6f0 0%, #fbf8f2 100%)" }}
    >
      <div className="absolute inset-0 marble opacity-50" aria-hidden="true" />
      <AmbientCanvas variant="dust" accent="#a8843a" density={0.4} className="opacity-30" />

      {/* Header */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-36">
        <div className="text-center mb-16 md:mb-24">
          <p className="ritual-eyebrow text-[10px] tracking-[0.4em] uppercase text-[#8a6a2c]/70 mb-4">
            The Journey
          </p>
          <h2
            className="ritual-title font-serif text-4xl md:text-6xl lg:text-7xl tracking-tight text-[#221d17]"
            style={{ fontWeight: 300 }}
          >
            The <span className="text-shimmer-gold">Ritual</span>
          </h2>
          <p className="ritual-sub text-sm text-[#6f6759]/70 font-light max-w-md mx-auto mt-6">
            From the earth to your skin — a journey of obsession, mastery, and
            uncompromising vision.
          </p>
          <div className="divider-elegant mx-auto mt-6" />
        </div>

        {/* Timeline */}
        <div className="max-w-6xl mx-auto relative">
          {/* Vertical line */}
          <div
            className="ritual-line absolute left-[3.4rem] md:left-[3.6rem] top-0 bottom-0 w-px bg-gradient-to-b from-[#c9a84c]/50 via-[#221d17]/10 to-transparent"
            aria-hidden="true"
          />

          {steps.map((step) => (
            <div key={step.num} className="ritual-step relative pl-20 md:pl-28 py-14 md:py-16">
              {/* Ghost numeral */}
              <span
                className="absolute right-0 top-6 font-serif text-8xl md:text-9xl leading-none select-none pointer-events-none"
                style={{ color: "rgba(34,29,23,0.05)" }}
                aria-hidden="true"
              >
                {step.num}
              </span>

              {/* Timeline node */}
              <div className="absolute left-14 md:left-16 top-16 -translate-x-1/2">
                <div
                  className="ritual-node w-3 h-3 rotate-45 border border-[#c9a84c]/60 bg-[#f9f6f0]"
                  style={{ boxShadow: `0 0 0 4px rgba(201,168,76,0.12)` }}
                  aria-hidden="true"
                />
              </div>

              <div className="flex items-center gap-3 mb-4">
                <span className="font-mono text-[10px] text-[#8a6a2c]/80">{step.num}</span>
                <div className="w-6 h-px" style={{ backgroundColor: `${step.accent}80` }} />
                <p className="text-[10px] tracking-[0.3em] uppercase" style={{ color: `${step.accent}` }}>
                  {step.preheading}
                </p>
              </div>

              <h3
                className="font-serif text-2xl md:text-3xl tracking-tight text-[#221d17] mb-4"
                style={{ fontWeight: 300 }}
              >
                {step.heading}
              </h3>

              <p className="text-sm text-[#6f6759]/75 font-light leading-relaxed max-w-2xl mb-6">
                {step.body}
              </p>

              <div className="ritual-stat flex items-baseline gap-3">
                <span className="font-serif text-2xl" style={{ color: `${step.accent}` }}>
                  {step.stat}
                </span>
                <span className="text-[10px] tracking-[0.15em] uppercase text-[#6f6759]/55">
                  {step.statLabel}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Closing CTA */}
      <div className="relative border-t border-[#221d17]/8">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(ellipse 50% 60% at 50% 100%, rgba(201,168,76,0.12) 0%, transparent 70%)",
          }}
          aria-hidden="true"
        />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <div className="section-divider divider-elegant mx-auto mb-10" />
          <p className="ritual-cta-eyebrow text-[10px] tracking-[0.4em] uppercase text-[#8a6a2c]/70 mb-4">
            The End Is Only the Beginning
          </p>
          <h3
            className="footer-statement font-serif text-3xl md:text-5xl tracking-tight text-[#221d17]/85 mb-10"
            style={{ fontWeight: 300 }}
          >
            Your Story Starts Here.
          </h3>
          <div className="ritual-cta-actions flex flex-wrap justify-center gap-4">
            <MagneticButton strength={0.15}>
              <button onClick={onOrderClick} className="btn-elegant" data-cursor="pointer">
                <span>Order Now</span>
              </button>
            </MagneticButton>
            <a
              href="tel:+923205038329"
              className="inline-flex items-center justify-center px-8 py-3.5 text-[10px] tracking-[0.2em] uppercase text-[#6f6759]/70 hover:text-[#8a6a2c] transition-colors duration-300 border border-transparent hover:border-[#c9a84c]/40"
              data-cursor="pointer"
            >
              Speak With Us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}